import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Brain, Loader2, Minus, Plus, ShieldCheck, X, Layers, Star, Crown } from "lucide-react";
import { api, toDecimal, fmtOdds } from "../lib/api";
import { useSlip } from "../context/SlipContext";
import { useAuth } from "../context/AuthContext";
import { ProjectedPick } from "./ProjectedPick";
import { GameLabel } from "./TeamLogo";
import { BestGames, BEST_LEVEL, rankGames } from "./BestGames";
import { labelFor } from "../lib/sports";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const MAX_LEGS = 15;

const toSelection = (l) => ({
  prop: {
    id: l.prop_id, player_name: l.player_name, metric: l.metric, line: l.line,
    over_odds: l.side === "OVER" ? l.odds : null, under_odds: l.side === "UNDER" ? l.odds : null,
    projection: l.projection, bookmaker: l.bookmaker, books_agree: l.books_agree, books_total: l.books_total,
    team_abbr: l.team_abbr, opponent_abbr: l.opponent_abbr, game_label: l.game_label, sport: l.sport,
  },
  lean: l.side,
});

const STAR = { gold: ["#F59E0B", "Superstar"], blue: ["#3B82F6", "Key player"] };

const RoleTag = ({ l, i }) => (
  <>
    {l.star && (
      <Star data-testid={`triple-star-${l.star}-${i}`} aria-label={STAR[l.star][1]}
        className="h-4 w-4 shrink-0" style={{ color: STAR[l.star][0], fill: STAR[l.star][0] }} />
    )}
    {l.role_label && (
      <span data-testid={`triple-role-${i}`} className={`rounded px-1.5 py-0.5 text-[10px] font-data font-bold ${
        l.starter ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#1B273E] text-slate-400"}`}>
        {l.role_label}
      </span>
    )}
  </>
);

const LegRow = ({ l, i, onRemove, best }) => (
  <div data-testid={`triple-leg-${i}`} data-best={!!best} className={`flex items-start gap-3 rounded-xl border p-3 ${
    best ? "border-[#F59E0B]/60 bg-[#F59E0B]/[0.06]" : "border-[#233248] bg-[#0B0F17]/60"}`}>
    <span className="font-data text-xs text-slate-500 w-5 pt-0.5">{i + 1}</span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <RoleTag l={l} i={i} />
        <span className="font-semibold text-white text-sm">{l.player_name}</span>
        <span className="font-data text-[10px] text-[#00E5FF]">{labelFor(l.sport)}</span>
        <GameLabel sport={l.sport} label={l.game_label} size="xs" className="text-[10px]" />
      </div>
      <ProjectedPick projection={l.projection} metric={l.metric} side={l.side} line={l.line} odds={l.odds}
        book={l.bookmaker} booksAgree={l.books_agree} booksTotal={l.books_total} />
      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
        <span className="rounded px-1.5 py-0.5 text-[10px] font-data font-bold bg-[#10B981]/15 text-[#10B981]">AI + Market {l.market_pct}%</span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-data font-bold ${
          l.trend_agree ? "bg-[#A78BFA]/20 text-[#C4B5FD]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
          Trend {l.trend_call} · exp {l.trend_expected} · {l.trend_confidence}%
        </span>
        <span className="text-[10px] text-slate-500 truncate max-w-[220px]" title={l.trend_analysis}>{l.trend_pattern}</span>
      </div>
    </div>
    <button data-testid={`triple-remove-${i}`} onClick={onRemove} aria-label="Remove leg"
      className="grid place-items-center h-7 w-7 rounded-md text-slate-500 hover:text-white hover:bg-[#1B273E] transition-colors">
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const TripleParlayBuilder = ({ open, onClose, matchupIds = [], scopeLabel, mode = "triple" }) => {
  const starters = mode === "starters";
  const title = starters ? "Starters-First Parlay" : "Triple-Check Parlay";
  const { user } = useAuth();
  const { buildParlay } = useSlip();
  const [strict, setStrict] = useState(true);
  const [nLegs, setNLegs] = useState(4);
  const [job, setJob] = useState(null);
  const [removed, setRemoved] = useState(new Set());
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);
  useEffect(() => {
    if (!open) clearInterval(timer.current);
  }, [open]);

  const build = async () => {
    if (!user) return toast.error("Sign in to build this parlay");
    setRemoved(new Set());
    try {
      const r = await api.post("/ai/triple-parlay", { matchup_ids: matchupIds, pool: 20, mode });
      setJob({ status: "running", done: 0, total: 0 });
      clearInterval(timer.current);
      let fails = 0;
      timer.current = setInterval(async () => {
        const s = await api.get(`/ai/triple-parlay/${r.data.job_id}`).catch(() => null);
        if (!s) {
          if (++fails >= 4) {
            clearInterval(timer.current);
            setJob({ status: "error" });
            toast.error("Lost connection while building — try again");
          }
          return;
        }
        fails = 0;
        setJob(s.data);
        if (s.data.status !== "running") clearInterval(timer.current);
        if (s.data.status === "error") toast.error(s.data.error || "Parlay build failed");
      }, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the build");
    }
  };

  const pool = (job?.legs || []).filter((l) => (strict ? l.trend_agree : true) && !removed.has(l.prop_id));
  const chosen = pool.slice(0, nLegs);
  const combined = chosen.reduce((a, l) => a * toDecimal(l.odds), 1);
  const american = combined >= 2 ? Math.round((combined - 1) * 100) : Math.round(-100 / (combined - 1 || 1));
  const running = job?.status === "running";

  const load = () => {
    buildParlay(chosen.map(toSelection), starters ? "Starters-First parlay" : "Triple-Check parlay");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={starters ? "starters-parlay-dialog" : "triple-parlay-dialog"} aria-describedby="triple-desc"
        className="bg-[#141C2B] border-[#233248] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#A78BFA]/20 text-[#A78BFA]">
              {starters ? <Crown className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
            </span>
            {title}
          </DialogTitle>
          <DialogDescription id="triple-desc" className="text-xs text-slate-400">
            AI projection + most sportsbooks + the AI trend read{scopeLabel ? ` · ${scopeLabel}` : ""}. Reads the 20 strongest agreement players.
            {starters && " Ordered starters first (QB → RB1 → TE1 → WR1… / PG → SG → SF → PF → C), then bench & backups, from real ESPN depth charts."}
          </DialogDescription>
          {starters && (
            <div data-testid="starters-legend" className="flex items-center gap-3 text-[11px] text-slate-400 pt-1">
              <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} /> Superstar</span>
              <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" style={{ color: "#3B82F6", fill: "#3B82F6" }} /> Key player</span>
              <span className="text-slate-500">· from team season stat leaders</span>
            </div>
          )}
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-[#233248] bg-[#0B0F17]/60 p-3">
            <div className="text-xs font-semibold text-slate-300 mb-2">Strictness</div>
            <div className="inline-flex w-full rounded-lg border border-[#233248] bg-[#0B0F17] p-0.5">
              {[[true, "All three agree"], [false, "Trend boosts rank"]].map(([v, label]) => (
                <button key={label} data-testid={`triple-mode-${v ? "strict" : "boost"}`} onClick={() => setStrict(v)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                    strict === v ? "bg-[#A78BFA] text-[#0B0F17]" : "text-slate-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#233248] bg-[#0B0F17]/60 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-300">How many legs</span>
              <div className="flex items-center gap-2">
                <button data-testid="triple-legs-dec" onClick={() => setNLegs(Math.max(1, nLegs - 1))}
                  className="h-7 w-7 grid place-items-center rounded-lg border border-[#233248] hover:border-[#A78BFA]/50"><Minus className="h-3 w-3" /></button>
                <span data-testid="triple-legs-count" className="font-data font-extrabold text-xl w-7 text-center text-[#A78BFA]">{nLegs}</span>
                <button data-testid="triple-legs-inc" onClick={() => setNLegs(Math.min(MAX_LEGS, nLegs + 1))}
                  className="h-7 w-7 grid place-items-center rounded-lg border border-[#233248] hover:border-[#A78BFA]/50"><Plus className="h-3 w-3" /></button>
              </div>
            </div>
            <input data-testid="triple-legs-slider" type="range" min={1} max={MAX_LEGS} value={nLegs}
              onChange={(e) => setNLegs(Number(e.target.value))} className="w-full accent-[#A78BFA] cursor-pointer" />
          </div>
        </div>

        <button data-testid="triple-build" onClick={build} disabled={running}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#A78BFA] to-[#00E5FF] text-[#0B0F17] font-bold text-sm py-2.5 hover:brightness-110 disabled:opacity-70 transition-[filter]">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
          {running ? `Reading player trends… ${job.done}/${job.total || "?"}` : job?.legs ? "Rebuild" : `Build ${title}`}
        </button>

        {job?.status === "done" && (
          <div className="space-y-2">
            {chosen.length === 0 ? (
              <p data-testid="triple-empty" className="text-sm text-slate-400 text-center py-4">
                No legs pass {strict ? "all three checks" : "the AI + Market check"} right now. Try "Trend boosts rank" or open more games.
              </p>
            ) : (
              <>
                {chosen.length < nLegs && (
                  <p className="text-[11px] text-[#F59E0B]">Only {chosen.length} legs qualify{strict ? " with all three agreeing" : ""}.</p>
                )}
                {(() => {
                  const games = rankGames(chosen);
                  const bestIds = new Set(games.filter((g) => g.chance >= BEST_LEVEL).map((g) => g.id));
                  return (
                    <>
                      <BestGames games={games} />
                      {chosen.map((l, i) => (
                        <LegRow key={l.prop_id} l={l} i={i} best={bestIds.has(l.matchup_id)}
                          onRemove={() => setRemoved((s) => new Set(s).add(l.prop_id))} />
                      ))}
                    </>
                  );
                })()}
                <div className="sticky bottom-0 flex items-center justify-between gap-2 flex-wrap rounded-xl border border-[#A78BFA]/30 bg-[#141C2B] p-3">
                  <div className="font-data text-sm whitespace-nowrap">
                    <span className="text-slate-400">{chosen.length} legs · </span>
                    <span className="text-[#A78BFA] font-bold">{fmtOdds(american)}</span>
                    <span className="text-slate-400"> · $10 pays </span>
                    <span className="text-white font-bold">${(10 * combined).toFixed(2)}</span>
                  </div>
                  <button data-testid="triple-add-to-slip" onClick={load}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#A78BFA] text-[#0B0F17] text-xs font-bold px-3 py-2 hover:brightness-110">
                    <Layers className="h-3.5 w-3.5" /> Add to slip
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
